import React, { useEffect, useMemo, useState } from "react";
import { api, formatDate, formatTRY, getWeekStart, todayISODate } from "@/lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Plus, Trash2, CalendarClock, CheckCircle2, Zap, Clock, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { TableWrap, Thead, EmptyRow } from "@/components/DataTable";

const weekLabel = (mondayISO) => {
  const d = new Date(mondayISO);
  const end = new Date(d);
  end.setDate(d.getDate() + 6);
  const fmt = (x) => x.toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" });
  return `${fmt(d)} – ${fmt(end)}`;
};

export default function Salaries() {
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [deleteId, setDeleteId] = useState(null);
  const [paying, setPaying] = useState(false);
  const [confirmPayOpen, setConfirmPayOpen] = useState(false);
  // Ödeme sayfasında düzenlenebilir tutarlar { empId: amount }
  const [payAmounts, setPayAmounts] = useState({});
  // Görüntülenen hafta — varsayılan takvim haftası; ödeme yapılınca otomatik ilerler
  const [displayWeekOffset, setDisplayWeekOffset] = useState(0);

  // Mesai / Avans dialog state
  const [extraOpen, setExtraOpen] = useState(false);
  const [extraForm, setExtraForm] = useState({
    id: null,
    type: "Mesai",
    employee_id: "",
    amount: 0,
    note: "",
    date: todayISODate(),
    week_start: "",
  });

  const load = async () => {
    const [s, e] = await Promise.all([api.get("/salaries"), api.get("/employees")]);
    setItems(s.data);
    setEmployees(e.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const calendarWeek = getWeekStart(); // Bu haftanın Pzt
  const activeEmployees = employees.filter((e) => e.active !== false);

  // Yardımcı: verilen ISO Monday + N gün
  const addDays = (isoDate, days) => {
    const d = new Date(isoDate);
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
  };
  // Görüntülenen hafta = takvim haftası + offset*7 gün
  const currentWeek = addDays(calendarWeek, displayWeekOffset * 7);
  const nextWeek = addDays(currentWeek, 7);

  // Görüntülenen haftada HAFTALIK ödemesi yapılmış çalışanların id'leri
  const paidThisWeek = useMemo(() => {
    const set = new Set();
    for (const s of items) {
      const t = s.type || "Haftalık";
      const w = (s.week_start || "").slice(0, 10);
      if (t === "Haftalık" && w === currentWeek) set.add(s.employee_id);
    }
    return set;
  }, [items, currentWeek]);

  // Görüntülenen haftadaki Mesai / Avans toplamları
  const weekExtrasByEmp = useMemo(() => {
    const map = {};
    for (const s of items) {
      if (s.type !== "Mesai" && s.type !== "Avans") continue;
      const w = (s.week_start || "").slice(0, 10);
      if (w !== currentWeek) continue;
      const key = s.type === "Mesai" ? "mesai" : "avans";
      if (!map[s.employee_id]) map[s.employee_id] = { mesai: 0, avans: 0 };
      map[s.employee_id][key] += Number(s.amount) || 0;
    }
    return map;
  }, [items, currentWeek]);

  const employeeWeekTotal = (emp) => {
    const ex = weekExtrasByEmp[emp.id] || { mesai: 0, avans: 0 };
    return (Number(emp.weekly_salary) || 0) + ex.mesai - ex.avans;
  };

  const unpaidEmployees = activeEmployees.filter((e) => !paidThisWeek.has(e.id));
  const weeklyTotalUnpaid = unpaidEmployees.reduce((s, e) => s + employeeWeekTotal(e), 0);
  const weeklyTotalAll = activeEmployees.reduce((s, e) => s + employeeWeekTotal(e), 0);

  const payAllThisWeek = async () => {
    if (unpaidEmployees.length === 0) {
      toast.info("Bu hafta için tüm çalışanlar ödendi.");
      return;
    }
    setPaying(true);
    try {
      await Promise.all(
        unpaidEmployees.map((emp) => {
          const ex = weekExtrasByEmp[emp.id] || { mesai: 0, avans: 0 };
          const total = employeeWeekTotal(emp);
          const parts = [];
          if (ex.mesai) parts.push(`mesai +${formatTRY(ex.mesai)}`);
          if (ex.avans) parts.push(`avans −${formatTRY(ex.avans)}`);
          const note = parts.length
            ? `Haftalık ödeme · ${parts.join(" + ")} dahil`
            : "Haftalık ödeme";
          return api.post("/salaries", {
            employee_id: emp.id,
            amount: total,
            week_start: currentWeek,
            type: "Haftalık",
            note,
            date: todayISODate(),
          });
        })
      );
      toast.success(`${unpaidEmployees.length} çalışan · toplam ${formatTRY(weeklyTotalUnpaid)} ödendi`);
      await load();
      // Ödeme sonrası otomatik yeni haftaya kay
      setDisplayWeekOffset((prev) => prev + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ödeme başarısız");
    } finally {
      setPaying(false);
    }
  };

  const del = async () => {
    await api.delete(`/salaries/${deleteId}`);
    toast.success("Silindi");
    setDeleteId(null);
    load();
  };

  const openExtra = (type) => {
    setExtraForm({
      id: null,
      type,
      employee_id: "",
      amount: 0,
      note: "",
      date: todayISODate(),
      week_start: currentWeek,
    });
    setExtraOpen(true);
  };

  const openEditExtra = (s) => {
    setExtraForm({
      id: s.id,
      type: s.type,
      employee_id: s.employee_id,
      amount: s.amount,
      note: s.note || "",
      date: (s.date || todayISODate()).slice(0, 10),
      week_start: (s.week_start || currentWeek).slice(0, 10),
    });
    setExtraOpen(true);
  };

  const saveExtra = async () => {
    if (!extraForm.employee_id) {
      toast.error("Çalışan seçin");
      return;
    }
    if (!Number(extraForm.amount)) {
      toast.error("Tutar girin");
      return;
    }
    try {
      const payload = {
        employee_id: extraForm.employee_id,
        amount: Number(extraForm.amount) || 0,
        week_start: extraForm.week_start || currentWeek,
        type: extraForm.type,
        note: extraForm.note,
        date: extraForm.date,
      };
      if (extraForm.id) {
        // Backend'de PUT yok — sil + yeniden yarat
        await api.delete(`/salaries/${extraForm.id}`);
        await api.post("/salaries", payload);
        toast.success(`${extraForm.type} güncellendi`);
      } else {
        await api.post("/salaries", payload);
        toast.success(`${extraForm.type} kaydedildi`);
      }
      setExtraOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    }
  };

  const monthTotal = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    return items
      .filter((s) => {
        const t = s.type || "Haftalık";
        if (t === "Mesai") return false; // Mesai Haftalık'a dahil edildiği için sayılmaz
        const d = new Date(s.date);
        return d.getFullYear() === y && d.getMonth() === m;
      })
      .reduce((sum, s) => sum + (s.amount || 0), 0);
  }, [items]);

  const weekly = items.filter((s) => (s.type || "Haftalık") === "Haftalık");
  const mesai = items.filter((s) => s.type === "Mesai");
  const avans = items.filter((s) => s.type === "Avans");

  return (
    <div data-testid="salaries-page" className="space-y-6">
      {/* SUMMARY CARDS */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
          <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Bu Ay Ödenen</p>
          <p className="font-display text-3xl font-bold text-slate-900 mt-2 font-mono-num" data-testid="month-salary-total">
            {formatTRY(monthTotal)}
          </p>
          <p className="text-xs text-slate-500 mt-1">Haftalık + avans (mesai haftalığa dahil)</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
          <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Haftalık Toplam</p>
          <p className="font-display text-3xl font-bold text-slate-900 mt-2 font-mono-num">
            {formatTRY(weeklyTotalAll)}
          </p>
          <p className="text-xs text-slate-500 mt-1">{activeEmployees.length} aktif çalışan</p>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-5">
          <div className="flex items-start gap-3">
            <CalendarClock className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-blue-900">Ödeme Dönemi</p>
              <p className="text-xs text-blue-800 mt-0.5">Cumartesi ödeme günü · Ödenen hafta kapanır, yeni giriş sonraki haftaya</p>
              <p className="text-[11px] text-blue-700 mt-1 font-mono-num">Pzt · {currentWeek}</p>
            </div>
          </div>
        </div>
      </div>

      <Tabs defaultValue="haftalik" className="w-full">
        <TabsList data-testid="salary-tabs" className="grid grid-cols-3 w-full max-w-xl">
          <TabsTrigger data-testid="tab-haftalik" value="haftalik">Haftalık Maaş</TabsTrigger>
          <TabsTrigger data-testid="tab-mesai" value="mesai">Mesai</TabsTrigger>
          <TabsTrigger data-testid="tab-avans" value="avans">Avans</TabsTrigger>
        </TabsList>

        {/* ============ HAFTALIK ============ */}
        <TabsContent value="haftalik" className="space-y-6 mt-6">
          {/* Bu haftanın bordrosu */}
          <section data-testid="weekly-payroll-card" className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
            <div className="p-5 border-b border-slate-100 bg-slate-50 flex items-center gap-3 flex-wrap">
              <div className="w-10 h-10 rounded-lg bg-amber-500 text-white flex items-center justify-center shrink-0">
                <Zap className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-[200px]">
                <h2 className="font-display text-base font-bold text-slate-900">Haftalık Bordro</h2>
                <p className="text-xs text-slate-500 font-mono-num">
                  {weekLabel(currentWeek)}
                  {displayWeekOffset > 0 && <span className="ml-2 text-amber-700 font-semibold">(+{displayWeekOffset} hafta ileri)</span>}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDisplayWeekOffset((p) => p - 1)}
                  data-testid="week-prev-btn"
                  title="Önceki hafta"
                  className="h-9 px-2"
                >
                  ◀
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDisplayWeekOffset(0)}
                  data-testid="week-today-btn"
                  disabled={displayWeekOffset === 0}
                  className="h-9 px-3 text-xs"
                >
                  Bugün
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDisplayWeekOffset((p) => p + 1)}
                  data-testid="week-next-btn"
                  title="Sonraki hafta"
                  className="h-9 px-2"
                >
                  ▶
                </Button>
                <div className="text-right pl-3 border-l border-slate-200">
                  <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Ödenecek</p>
                  <p data-testid="weekly-unpaid-total" className="font-display text-lg font-bold font-mono-num text-slate-900">
                    {formatTRY(weeklyTotalUnpaid)}
                  </p>
                </div>
              </div>
            </div>

            {activeEmployees.length === 0 ? (
              <div className="p-6 text-sm text-slate-500 text-center">Aktif çalışan yok. Önce Çalışanlar sayfasından ekleyin.</div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-600">
                      <tr>
                        <th className="text-left px-4 py-3 font-semibold">Çalışan</th>
                        <th className="text-right px-3 py-3 font-semibold">Baz Maaş</th>
                        <th className="text-right px-3 py-3 font-semibold">+ Mesai</th>
                        <th className="text-right px-3 py-3 font-semibold">− Avans</th>
                        <th className="text-right px-4 py-3 font-semibold">Toplam</th>
                        <th className="text-center px-4 py-3 font-semibold">Durum</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeEmployees.map((emp) => {
                        const isPaid = paidThisWeek.has(emp.id);
                        const ex = weekExtrasByEmp[emp.id] || { mesai: 0, avans: 0 };
                        const total = employeeWeekTotal(emp);
                        return (
                          <tr key={emp.id} className="border-t border-slate-100" data-testid={`payroll-row-${emp.id}`}>
                            <td className="px-4 py-3 font-semibold text-slate-900">
                              {emp.name}
                              <div className="text-xs text-slate-500 font-normal">{emp.role || "Usta"}</div>
                              <div className="text-[10px] text-blue-700 font-mono-num mt-0.5">
                                Hafta: {currentWeek}
                              </div>
                            </td>
                            <td className="px-3 py-3 text-right font-mono-num text-slate-700">
                              {formatTRY(emp.weekly_salary)}
                            </td>
                            <td className="px-3 py-3 text-right font-mono-num" data-testid={`emp-mesai-${emp.id}`}>
                              {ex.mesai > 0 ? (
                                <span className="text-blue-700 font-semibold">+{formatTRY(ex.mesai)}</span>
                              ) : (
                                <span className="text-slate-300">—</span>
                              )}
                            </td>
                            <td className="px-3 py-3 text-right font-mono-num" data-testid={`emp-avans-${emp.id}`}>
                              {ex.avans > 0 ? (
                                <span className="text-purple-700 font-semibold">−{formatTRY(ex.avans)}</span>
                              ) : (
                                <span className="text-slate-300">—</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right font-mono-num font-bold text-slate-900" data-testid={`emp-total-${emp.id}`}>
                              {formatTRY(total)}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {isPaid ? (
                                <span
                                  data-testid={`paid-status-${emp.id}`}
                                  className="inline-flex items-center gap-1.5 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-2.5 py-1.5 text-xs font-semibold"
                                >
                                  <CheckCircle2 className="w-3.5 h-3.5" /> Ödendi
                                </span>
                              ) : (
                                <span
                                  data-testid={`pending-status-${emp.id}`}
                                  className="inline-flex items-center gap-1.5 text-slate-600 bg-slate-100 border border-slate-200 rounded-md px-2.5 py-1.5 text-xs font-semibold"
                                >
                                  <Clock className="w-3.5 h-3.5" /> Bekliyor
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* En altta tek büyük buton */}
                <div className="p-5 bg-gradient-to-br from-amber-50 to-white border-t border-slate-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <p className="text-sm text-slate-700">
                      {unpaidEmployees.length > 0
                        ? <>Bu hafta ödemesi bekleyen <span className="font-semibold text-slate-900">{unpaidEmployees.length}</span> çalışan var.</>
                        : <>Bu hafta için tüm çalışanlar ödendi. ✓</>}
                    </p>
                    {unpaidEmployees.length > 0 && (
                      <p className="text-xs text-slate-500 mt-1">
                        Toplam: <span className="font-semibold text-slate-900 font-mono-num">{formatTRY(weeklyTotalUnpaid)}</span>
                      </p>
                    )}
                  </div>
                  <Button
                    data-testid="pay-all-week-btn"
                    onClick={payAllThisWeek}
                    disabled={unpaidEmployees.length === 0 || paying}
                    className="bg-amber-500 hover:bg-amber-600 text-white h-11 px-6 text-base font-semibold shadow-sm w-full sm:w-auto"
                  >
                    <Zap className="w-4 h-4 mr-2" />
                    {paying ? "Ödeniyor…" : "Haftalık Toplu Ödeme Yap"}
                  </Button>
                </div>
              </>
            )}
          </section>

          {/* Haftalık ödeme geçmişi — HAFTA HAFTA gruplu */}
          <div>
            <h3 className="font-display text-base font-bold text-slate-900 mb-3">Haftalık Ödeme Geçmişi</h3>
            {weekly.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-lg p-6 text-center text-sm text-slate-500">
                Henüz haftalık ödeme yok.
              </div>
            ) : (
              <div className="space-y-4">
                {(() => {
                  // Ödemeleri hafta bazında grupla
                  const byWeek = {};
                  for (const s of weekly) {
                    const w = (s.week_start || "").slice(0, 10) || "—";
                    if (!byWeek[w]) byWeek[w] = [];
                    byWeek[w].push(s);
                  }
                  const weekKeys = Object.keys(byWeek).sort((a, b) => b.localeCompare(a));
                  return weekKeys.map((wk) => {
                    const rows = byWeek[wk];
                    const weekTotal = rows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
                    return (
                      <div key={wk} data-testid={`weekly-group-${wk}`} className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
                        <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50 border-b border-slate-200">
                          <div>
                            <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Hafta</p>
                            <p className="font-display text-sm font-bold text-slate-900 font-mono-num">
                              {wk !== "—" ? weekLabel(wk) : "Belirsiz Hafta"}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Toplam · {rows.length} ödeme</p>
                            <p className="font-display text-base font-bold font-mono-num text-emerald-700">{formatTRY(weekTotal)}</p>
                          </div>
                        </div>
                        <table className="w-full text-sm">
                          <thead className="bg-white text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-100">
                            <tr>
                              <th className="text-left px-4 py-2 font-semibold">Ödeme Tarihi</th>
                              <th className="text-left px-4 py-2 font-semibold">Çalışan</th>
                              <th className="text-right px-4 py-2 font-semibold">Tutar</th>
                              <th className="text-left px-4 py-2 font-semibold">Not</th>
                              <th className="px-4 py-2"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((s) => (
                              <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`salary-row-${s.id}`}>
                                <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap">{formatDate(s.date)}</td>
                                <td className="px-4 py-3 font-semibold text-slate-900">{s.employee_name}</td>
                                <td className="px-4 py-3 text-sm font-mono-num font-semibold text-slate-900 text-right">{formatTRY(s.amount)}</td>
                                <td className="px-4 py-3 text-sm text-slate-600 max-w-xs truncate">{s.note || "-"}</td>
                                <td className="px-4 py-3 text-right">
                                  <div className="flex items-center justify-end gap-1">
                                    <Button
                                      data-testid={`edit-salary-${s.id}`}
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => openEditExtra({ ...s, type: "Haftalık" })}
                                      className="h-8 w-8"
                                      title="Düzenle"
                                    >
                                      <CheckCircle2 className="w-4 h-4 text-slate-500" />
                                    </Button>
                                    <Button
                                      data-testid={`delete-salary-${s.id}`}
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => setDeleteId(s.id)}
                                      className="h-8 w-8 hover:bg-rose-50"
                                      title="Sil"
                                    >
                                      <Trash2 className="w-4 h-4 text-rose-600" />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  });
                })()}
              </div>
            )}
          </div>
        </TabsContent>

        {/* ============ MESAI ============ */}
        <TabsContent value="mesai" className="space-y-4 mt-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h3 className="font-display text-base font-bold text-slate-900">Mesai Ödemeleri</h3>
              <p className="text-xs text-slate-500">Her mesai kaydı seçilen haftaya yazılır ve o haftanın bordrosuna eklenir</p>
            </div>
            <Button data-testid="add-mesai-btn" onClick={() => openExtra("Mesai")} className="bg-slate-900 hover:bg-slate-800">
              <Plus className="w-4 h-4 mr-2" /> Yeni Mesai
            </Button>
          </div>
          <TableWrap>
            <Thead columns={["Tarih", "Çalışan", "Hafta", "Tutar", "Not", ""]} />
            <tbody>
              {mesai.length === 0 && (
                <EmptyRow colSpan={6} testId="mesai-empty" message="Henüz mesai kaydı yok." />
              )}
              {mesai.map((s) => (
                <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`mesai-row-${s.id}`}>
                  <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap">{formatDate(s.date)}</td>
                  <td className="px-4 py-3 font-semibold text-slate-900">{s.employee_name}</td>
                  <td className="px-4 py-3 text-xs text-slate-600 font-mono-num whitespace-nowrap">{(s.week_start || "").slice(0, 10)}</td>
                  <td className="px-4 py-3 text-sm font-mono-num font-semibold text-slate-900">{formatTRY(s.amount)}</td>
                  <td className="px-4 py-3 text-sm text-slate-600 max-w-xs truncate">{s.note || "-"}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button data-testid={`edit-mesai-${s.id}`} variant="ghost" size="icon" onClick={() => openEditExtra(s)} className="h-8 w-8">
                        <CheckCircle2 className="w-4 h-4 text-slate-500" />
                      </Button>
                      <Button data-testid={`delete-mesai-${s.id}`} variant="ghost" size="icon" onClick={() => setDeleteId(s.id)} className="h-8 w-8 hover:bg-rose-50">
                        <Trash2 className="w-4 h-4 text-rose-600" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        </TabsContent>

        {/* ============ AVANS ============ */}
        <TabsContent value="avans" className="space-y-4 mt-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h3 className="font-display text-base font-bold text-slate-900">Avans Ödemeleri</h3>
              <p className="text-xs text-slate-500">Her avans kaydı seçilen haftaya yazılır ve o haftanın bordrosundan <b>düşülür</b></p>
            </div>
            <Button data-testid="add-avans-btn" onClick={() => openExtra("Avans")} className="bg-slate-900 hover:bg-slate-800">
              <Plus className="w-4 h-4 mr-2" /> Yeni Avans
            </Button>
          </div>
          <TableWrap>
            <Thead columns={["Tarih", "Çalışan", "Hafta", "Tutar", "Not", ""]} />
            <tbody>
              {avans.length === 0 && (
                <EmptyRow colSpan={6} testId="avans-empty" message="Henüz avans kaydı yok." />
              )}
              {avans.map((s) => (
                <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`avans-row-${s.id}`}>
                  <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap">{formatDate(s.date)}</td>
                  <td className="px-4 py-3 font-semibold text-slate-900">{s.employee_name}</td>
                  <td className="px-4 py-3 text-xs text-slate-600 font-mono-num whitespace-nowrap">{(s.week_start || "").slice(0, 10)}</td>
                  <td className="px-4 py-3 text-sm font-mono-num font-semibold text-slate-900">{formatTRY(s.amount)}</td>
                  <td className="px-4 py-3 text-sm text-slate-600 max-w-xs truncate">{s.note || "-"}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button data-testid={`edit-avans-${s.id}`} variant="ghost" size="icon" onClick={() => openEditExtra(s)} className="h-8 w-8">
                        <CheckCircle2 className="w-4 h-4 text-slate-500" />
                      </Button>
                      <Button data-testid={`delete-avans-${s.id}`} variant="ghost" size="icon" onClick={() => setDeleteId(s.id)} className="h-8 w-8 hover:bg-rose-50">
                        <Trash2 className="w-4 h-4 text-rose-600" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        </TabsContent>
      </Tabs>

      {/* Confirm Pay-all dialog */}
      {/* Mesai/Avans dialog */}
      <Dialog open={extraOpen} onOpenChange={setExtraOpen}>
        <DialogContent data-testid="extra-salary-dialog" className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">{extraForm.id ? `${extraForm.type} Düzenle` : `Yeni ${extraForm.type}`}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Çalışan</Label>
              <Select value={extraForm.employee_id} onValueChange={(v) => setExtraForm({ ...extraForm, employee_id: v })}>
                <SelectTrigger data-testid="extra-employee-select">
                  <SelectValue placeholder="Çalışan seçin" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tutar (₺)</Label>
                <Input
                  data-testid="extra-amount-input"
                  type="number"
                  step="0.01"
                  value={extraForm.amount}
                  onChange={(e) => setExtraForm({ ...extraForm, amount: e.target.value })}
                  className="font-mono-num"
                />
              </div>
              <div>
                <Label>Tarih</Label>
                <Input
                  data-testid="extra-date-input"
                  type="date"
                  value={extraForm.date}
                  onChange={(e) => setExtraForm({ ...extraForm, date: e.target.value })}
                />
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <Label className="text-[11px] uppercase tracking-widest text-blue-800 font-semibold">
                Hangi Haftaya Yazılsın
              </Label>
              <Input
                data-testid="extra-week-input"
                type="date"
                value={extraForm.week_start}
                onChange={(e) => setExtraForm({ ...extraForm, week_start: getWeekStart(new Date(e.target.value)) })}
                className="font-mono-num mt-1 bg-white"
              />
              <p className="text-[11px] text-blue-700 mt-1">
                Otomatik olarak seçilen tarihin Pazartesi'sine kayar. Geçmiş haftaya da giriş yapabilirsiniz.
              </p>
            </div>
            <div>
              <Label>Not</Label>
              <Input
                data-testid="extra-note-input"
                value={extraForm.note}
                onChange={(e) => setExtraForm({ ...extraForm, note: e.target.value })}
                placeholder={extraForm.type === "Mesai" ? "Örn: 8 saat cumartesi mesaisi" : "Örn: Ay ortası avans"}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExtraOpen(false)}>İptal</Button>
            <Button data-testid="save-extra-btn" onClick={saveExtra} className="bg-slate-900 hover:bg-slate-800">
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Kayıt silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>Bu işlem geri alınamaz.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-salary" onClick={del} className="bg-rose-600 hover:bg-rose-700">
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
